import { ArrowRight, CheckCircle, FileText, Info, XCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExtractionJob } from '@/services/aiExtractionService';
import { Button } from '@/components/Common/button';

import { domainService } from '@/services/domainService';
import { useTranslation } from 'react-i18next';

interface AIExtractionJobDetailProps {
  job: ExtractionJob;
  onApprove: () => void;
  onReject: () => void;
  isProcessing?: boolean;
}

const AIExtractionJobDetail: React.FC<AIExtractionJobDetailProps> = ({
  job,
  onApprove,
  onReject,
  isProcessing = false,
}) => {
  const navigate = useNavigate();
  const [domainName, setDomainName] = useState<string | null>(null);
  const [existingEntity, setExistingEntity] = useState<any | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const loadDomainName = async () => {
      const domainId = job.context?.domain_id;
      if (domainId && typeof domainId === 'string') {
        try {
          const domain = await domainService.getDomainDetails(domainId);
          setDomainName(domain.name);
        } catch (error) {
          console.error('Error loading domain:', error);
        }
      }
    };

    loadDomainName();
  }, [job.context?.domain_id]);

  // Load existing entity data for update actions
  useEffect(() => {
    const loadExistingEntity = async () => {
      if (job.status !== 'review_required') return;
      
      const action = job.requested_action;
      
      if (action === 'update_usecase' && job.context?.usecase_id) {
        setLoadingExisting(true);
        try {
          const { useCaseService } = await import('@/services/useCaseService');
          const usecase = await useCaseService.getUseCaseDetails(job.context.usecase_id);
          setExistingEntity(usecase);
        } catch (error) {
          console.error('Error loading existing usecase:', error);
        } finally {
          setLoadingExisting(false);
        }
      } else if (action === 'update_cadrage' && job.context?.cadrage_id) {
        setLoadingExisting(true);
        try {
          const { cadrageService } = await import('@/services/cadrageService');
          const cadrage = await cadrageService.getCadrage(job.context.cadrage_id);
          setExistingEntity(cadrage);
        } catch (error) {
          console.error('Error loading existing cadrage:', error);
        } finally {
          setLoadingExisting(false);
        }
      }
    };

    loadExistingEntity();
  }, [job.status, job.requested_action, job.context?.usecase_id, job.context?.cadrage_id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper to render before/after comparison for updates
  const renderFieldComparison = (label: string, before: any, after: any) => {
    // Skip if after value is not provided
    if (after === undefined || after === null) return null;
    
    const hasChanged = JSON.stringify(before) !== JSON.stringify(after);
    
    return (
      <div className="mb-3">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className={`text-sm p-2 rounded border ${hasChanged ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="text-xs text-muted-foreground mb-1">{t('aiExtraction.jobDetail.before')}</div>
            <div className="text-foreground">{before || <span className="text-gray-400 italic">{t('aiExtraction.jobDetail.empty')}</span>}</div>
          </div>
          <div className={`text-sm p-2 rounded border ${hasChanged ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="text-xs text-muted-foreground mb-1">{t('aiExtraction.jobDetail.after')}</div>
            <div className="text-foreground font-medium">{after || <span className="text-gray-400 italic">{t('aiExtraction.jobDetail.empty')}</span>}</div>
          </div>
        </div>
      </div>
    );
  };

  // Get navigation info for the created/updated entity
  const getEntityNavigation = () => {
    const action = job.requested_action;
    const context = job.context || {};
    const prep = job.preparation_result;
    
    // Try to get created IDs from extraction_result if available
    const extractionResult = job.extraction_result || {};
    
    // Debug logging
    console.log('getEntityNavigation debug:', {
      action,
      context,
      extractionResult,
      jobStatus: job.status
    });
    
    if (action === 'create_usecase' || action === 'update_usecase') {
      const usecaseId = extractionResult.usecase_id || context.usecase_id;
      console.log('UseCase ID found:', usecaseId);
      if (usecaseId) {
        return {
          path: `/cas-usage/${usecaseId}`,
          label: t('aiExtraction.jobDetail.viewUseCase'),
          entityName: prep?.summary?.usecase_name || t('aiExtraction.jobDetail.useCaseFallback')
        };
      }
    } else if (action === 'create_cadrage' || action === 'update_cadrage') {
      const cadrageId = extractionResult.cadrage_id || context.cadrage_id;
      console.log('Cadrage ID found:', cadrageId);
      if (cadrageId) {
        return {
          path: `/cadrages/${cadrageId}`,
          label: t('aiExtraction.jobDetail.viewCadrage'),
          entityName: prep?.summary?.cadrage_name || t('aiExtraction.jobDetail.cadrageFallback')
        };
      }
      // Fallback to use case if cadrage ID is not available
      const usecaseId = extractionResult.usecase_id || context.usecase_id;
      if (usecaseId) {
        return {
          path: `/cas-usage/${usecaseId}`,
          label: t('aiExtraction.jobDetail.viewUseCase'),
          entityName: prep?.summary?.usecase_name || t('aiExtraction.jobDetail.useCaseFallback')
        };
      }
    }
    
    console.log('No navigation found');
    return null;
  };

  const renderPreparationResult = () => {
    if (!job.preparation_result) return null;

    const prep = job.preparation_result;
    const summary = prep.summary || {};
    const payloads = prep.payloads || {};

    return (
      <div className="space-y-4">
        {/* Summary */}
        {summary.operation && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">
              {t('aiExtraction.jobDetail.summaryTitle')}
            </h4>
            <p className="text-sm text-muted-foreground">
              <strong>{t('aiExtraction.jobDetail.summaryOperation')}</strong>{' '}
              {summary.operation.replace(/_/g, ' ')}
            </p>
            {(summary.domain_name || domainName) && (
              <p className="text-sm text-muted-foreground">
                <strong>{t('aiExtraction.jobDetail.summaryDomain')}</strong>{' '}
                {summary.domain_name || domainName || t('global.notAvailable')}
              </p>
            )}
            {summary.usecase_name && (
              <p className="text-sm text-muted-foreground">
                <strong>{t('aiExtraction.jobDetail.summaryUseCase')}</strong>{' '}
                {summary.usecase_name}
              </p>
            )}
            {summary.cadrage_name && (
              <p className="text-sm text-muted-foreground">
                <strong>{t('aiExtraction.jobDetail.summaryCadrage')}</strong>{' '}
                {summary.cadrage_name}
              </p>
            )}
            {summary.confidence && (
              <p className="text-sm text-muted-foreground">
                <strong>{t('aiExtraction.jobDetail.summaryConfidence')}</strong>{' '}
                {(summary.confidence * 100).toFixed(1)}%
              </p>
            )}
          </div>
        )}

        {/* Defaulted fields (required fields filled with defaults when LLM schema was invalid) */}
        {prep.defaulted_fields && Array.isArray(prep.defaulted_fields) && prep.defaulted_fields.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Info className="h-5 w-5 text-amber-600 shrink-0" />
              {t('aiExtraction.jobDetail.defaultedFieldsTitle')}
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {t('aiExtraction.jobDetail.defaultedFieldsIntro')}
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              {prep.defaulted_fields.map((item: { path?: string; default_value?: unknown }, idx: number) => (
                <li key={idx}>
                  <code className="text-foreground bg-muted px-1 rounded">{item.path ?? '—'}</code>
                  {' → '}
                  <span className="text-foreground">{String(item.default_value ?? '')}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warnings */}
        {prep.warnings && prep.warnings.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">
              {t('aiExtraction.jobDetail.warningsTitle')}
            </h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              {prep.warnings.map((warning: string, idx: number) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        {/* File Attachment Notice */}
        {job.context?.file_path && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-green-600" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground text-sm">
                  {t('aiExtraction.jobDetail.documentSource')} {job.context.original_filename || job.document_name}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('aiExtraction.jobDetail.willBeAttached')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payloads Preview as Cards */}
        {Object.keys(payloads).length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground">
              {t('aiExtraction.jobDetail.payloadsTitle')}
            </h4>
            {payloads.domain && (
              <div className="bg-card border-2 border-primary/20 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-lg font-semibold text-foreground">
                    {t('aiExtraction.jobDetail.domainCardTitle')}
                  </h5>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                    {t('aiExtraction.jobDetail.domainCardBadge')}
                  </span>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      {t('global.name')}
                    </label>
                    <p className="text-sm text-foreground font-medium">{payloads.domain.name || t('global.notAvailable')}</p>
                  </div>
                  {payloads.domain.description && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">
                        {t('global.description')}
                      </label>
                      <p className="text-sm text-foreground">{payloads.domain.description}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {(payloads.usecase || payloads.usecase_updates) && (
              <div className="bg-card border-2 border-blue-500/20 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-lg font-semibold text-foreground">
                    {job.requested_action === 'update_usecase' ? t('aiExtraction.jobDetail.useCaseUpdate') : t('aiExtraction.jobDetail.useCaseCardTitle')}
                  </h5>
                  <span className="text-xs bg-blue-500/10 text-blue-600 px-2 py-1 rounded">
                    {job.requested_action === 'update_usecase' ? t('aiExtraction.jobDetail.update') : t('aiExtraction.jobDetail.useCaseCardBadge')}
                  </span>
                </div>
                
                {loadingExisting && job.requested_action === 'update_usecase' && (
                  <div className="text-sm text-muted-foreground mb-3">{t('aiExtraction.jobDetail.loadingExistingData')}</div>
                )}
                
                <div className="space-y-2">
                  {(summary.domain_name || domainName) && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">
                        {t('global.domain')}
                      </label>
                      <p className="text-sm text-foreground font-medium">{summary.domain_name || domainName}</p>
                    </div>
                  )}
                  
                  {job.requested_action === 'update_usecase' && existingEntity ? (
                    <>
                      {/* Show before/after comparison for updates */}
                      {Object.entries(payloads.usecase_updates || {}).map(([usecaseId, updates]: [string, any]) => (
                        <div key={usecaseId}>
                          {updates.name && renderFieldComparison(t('global.name'), existingEntity.name, updates.name)}
                          {updates.description && renderFieldComparison(t('global.description'), existingEntity.description, updates.description)}
                          {updates.business_need && renderFieldComparison(t('aiExtraction.jobDetail.businessNeed'), existingEntity.business_need, updates.business_need)}
                          {updates.short_description && renderFieldComparison(t('aiExtraction.jobDetail.shortDescription'), existingEntity.short_description, updates.short_description)}
                          {updates.input && renderFieldComparison(t('aiExtraction.jobDetail.input'), existingEntity.input, updates.input)}
                          {updates.output && renderFieldComparison(t('aiExtraction.jobDetail.output'), existingEntity.output, updates.output)}
                          {updates.technical_elements && renderFieldComparison(t('aiExtraction.jobDetail.technicalElements'), existingEntity.technical_elements, updates.technical_elements)}
                          {updates.recommendation && renderFieldComparison(t('useCases.recommendation'), existingEntity.recommendation, updates.recommendation)}
                          {updates.synthese && renderFieldComparison(t('aiExtraction.jobDetail.synthese'), existingEntity.synthese, updates.synthese)}
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* Show normal view for creation */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">
                          {t('aiExtraction.jobDetail.useCaseTitle')}
                        </label>
                        <p className="text-sm text-foreground font-medium">{payloads.usecase?.title || payloads.usecase?.name || t('global.notAvailable')}</p>
                      </div>
                      {payloads.usecase?.description && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">
                            {t('aiExtraction.jobDetail.useCaseDescription')}
                          </label>
                          <p className="text-sm text-foreground">{payloads.usecase.description}</p>
                        </div>
                      )}
                      {payloads.usecase?.business_need && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">
                            {t('aiExtraction.jobDetail.businessNeed')}
                          </label>
                          <p className="text-sm text-foreground">{payloads.usecase.business_need}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {(payloads.cadrage || payloads.cadrage_updates) && (
              <div className="bg-card border-2 border-purple-500/20 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-lg font-semibold text-foreground">
                    {job.requested_action === 'update_cadrage' ? t('aiExtraction.jobDetail.cadrageUpdate') : t('aiExtraction.jobDetail.cadrageCardTitle')}
                  </h5>
                  <span className="text-xs bg-purple-500/10 text-purple-600 px-2 py-1 rounded">
                    {job.requested_action === 'update_cadrage' ? t('aiExtraction.jobDetail.update') : t('aiExtraction.jobDetail.cadrageCardBadge')}
                  </span>
                </div>
                
                {loadingExisting && job.requested_action === 'update_cadrage' && (
                  <div className="text-sm text-muted-foreground mb-3">{t('aiExtraction.jobDetail.loadingExistingData')}</div>
                )}
                
                <div className="space-y-2">
                  {(summary.domain_name || domainName) && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">
                        {t('aiExtraction.jobDetail.cadrageDomain')}
                      </label>
                      <p className="text-sm text-foreground font-medium">{summary.domain_name || domainName}</p>
                    </div>
                  )}
                  {summary.usecase_name && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">
                        {t('aiExtraction.jobDetail.cadrageUseCase')}
                      </label>
                      <p className="text-sm text-foreground font-medium">{summary.usecase_name}</p>
                    </div>
                  )}
                  
                  {job.requested_action === 'update_cadrage' && existingEntity ? (
                    <>
                      {/* Show before/after comparison for updates */}
                      {Object.entries(payloads.cadrage_updates || {}).map(([cadrageId, updates]: [string, any]) => (
                        <div key={cadrageId}>
                          {updates.name && renderFieldComparison(t('global.name'), existingEntity.name, updates.name)}
                          {updates.description && renderFieldComparison(t('global.description'), existingEntity.description, updates.description)}
                          {updates.synthese && renderFieldComparison(t('global.description'), existingEntity.synthese, updates.synthese)}
                          {updates.content && renderFieldComparison(t('global.description'), existingEntity.content, updates.content)}
                          {updates.type && renderFieldComparison(t('global.category'), existingEntity.type, updates.type)}
                          {updates.company_name && renderFieldComparison(t('global.client'), existingEntity.company_name, updates.company_name)}
                          {updates.architecture_notes && renderFieldComparison(t('cadrageDetail.architectureNotes'), existingEntity.architecture_notes, updates.architecture_notes)}
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* Show normal view for creation */}
                      {payloads.cadrage?.name && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">
                            {t('global.name')}
                          </label>
                          <p className="text-sm text-foreground font-medium">{payloads.cadrage.name}</p>
                        </div>
                      )}
                      {payloads.cadrage?.description && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">
                            {t('global.description')}
                          </label>
                          <p className="text-sm text-foreground">{payloads.cadrage.description}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Personas Section (for UseCases) */}
            {payloads.personas && payloads.personas.length > 0 && (
              <div className="bg-card border-2 border-green-500/20 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-lg font-semibold text-foreground">
                    {t('aiExtraction.jobDetail.personasIdentified', { count: payloads.personas.length })}
                  </h5>
                  <span className="text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded">
                    {t('aiExtraction.jobDetail.useCaseCardBadge')}
                  </span>
                </div>
                <div className="space-y-3">
                  {payloads.personas.map((persona: any, idx: number) => (
                    <div key={idx} className="bg-background/50 rounded-lg p-3 border border-border">
                      <h6 className="font-semibold text-foreground">
                        {persona.name}{persona.role && ` - ${persona.role}`}
                      </h6>
                      {persona.description && (
                        <p className="text-sm text-muted-foreground mt-1">{persona.description}</p>
                      )}
                      {persona.objectif && (
                        <p className="text-sm text-foreground mt-2">
                          <strong className="text-green-600">{t('aiExtraction.jobDetail.goal')}</strong> {persona.objectif}
                        </p>
                      )}
                      {persona.frustration && (
                        <p className="text-sm text-foreground mt-1">
                          <strong className="text-red-600">{t('aiExtraction.jobDetail.frustration')}</strong> {persona.frustration}
                        </p>
                      )}
                      {persona.citation && (
                        <blockquote className="text-sm italic text-muted-foreground mt-2 pl-3 border-l-2 border-border">
                          "{persona.citation}"
                        </blockquote>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Qualification Section (for UseCases) */}
            {payloads.qualification && (
              <div className="bg-card border-2 border-yellow-500/20 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-lg font-semibold text-foreground">
                    {t('aiExtraction.jobDetail.qualificationScoring')}
                  </h5>
                  <span className="text-xs bg-yellow-500/10 text-yellow-600 px-2 py-1 rounded">
                    {t('aiExtraction.jobDetail.aiGenerated')}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {payloads.qualification.value_score && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">{t('aiExtraction.jobDetail.businessValue')}</label>
                      <p className="text-sm text-foreground font-medium">{payloads.qualification.value_score}</p>
                    </div>
                  )}
                  {payloads.qualification.complexity_score && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">{t('aiExtraction.jobDetail.complexity')}</label>
                      <p className="text-sm text-foreground font-medium">{payloads.qualification.complexity_score}</p>
                    </div>
                  )}
                  {payloads.qualification.moscow && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">{t('aiExtraction.jobDetail.priorityMoscow')}</label>
                      <p className="text-sm text-foreground font-medium">{payloads.qualification.moscow}</p>
                    </div>
                  )}
                </div>
                {payloads.qualification.recommendations && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-muted-foreground">{t('aiExtraction.jobDetail.recommendations')}</label>
                    <p className="text-sm text-foreground">{payloads.qualification.recommendations}</p>
                  </div>
                )}
                {payloads.qualification.notes && (
                  <div className="mt-2">
                    <label className="text-xs font-medium text-muted-foreground">{t('global.notes')}</label>
                    <p className="text-sm text-foreground">{payloads.qualification.notes}</p>
                  </div>
                )}
                <p className="text-xs text-yellow-600 mt-3 flex items-center">
                  <Info className="h-4 w-4 mr-1" />
                  {t('aiExtraction.jobDetail.qualificationReviewNote')}
                </p>
              </div>
            )}
            
            {/* Blockers Section (for Cadrages) */}
            {payloads.blockers && payloads.blockers.length > 0 && (
              <div className="bg-card border-2 border-red-500/20 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-lg font-semibold text-foreground">
                    {t('aiExtraction.jobDetail.blockersIdentified', { count: payloads.blockers.length })}
                  </h5>
                  <span className="text-xs bg-red-500/10 text-red-600 px-2 py-1 rounded">
                    {t('aiExtraction.jobDetail.useCaseCardBadge')}
                  </span>
                </div>
                <div className="space-y-2">
                  {payloads.blockers.map((blocker: any, idx: number) => (
                    <div key={idx} className={`bg-background/50 rounded-lg p-3 border-l-4 ${
                      blocker.criticite === 'critical' ? 'border-red-600' :
                      blocker.criticite === 'high' ? 'border-orange-500' :
                      blocker.criticite === 'medium' ? 'border-yellow-500' :
                      'border-blue-500'
                    }`}>
                      <div className="flex items-start justify-between">
                        <p className="text-sm text-foreground flex-1">{blocker.description}</p>
                        {blocker.criticite && (
                          <span className={`text-xs px-2 py-0.5 rounded ml-2 ${
                            blocker.criticite === 'critical' ? 'bg-red-100 text-red-800' :
                            blocker.criticite === 'high' ? 'bg-orange-100 text-orange-800' :
                            blocker.criticite === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {blocker.criticite}
                          </span>
                        )}
                      </div>
                      {blocker.strategie_mitigation && (
                        <p className="text-sm text-muted-foreground mt-2">
                          <strong>{t('aiExtraction.jobDetail.mitigation')}</strong> {blocker.strategie_mitigation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Deliverables Section (for Cadrages) */}
            {payloads.deliverables && payloads.deliverables.length > 0 && (
              <div className="bg-card border-2 border-indigo-500/20 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-lg font-semibold text-foreground">
                    {t('aiExtraction.jobDetail.deliverablesExpected', { count: payloads.deliverables.length })}
                  </h5>
                  <span className="text-xs bg-indigo-500/10 text-indigo-600 px-2 py-1 rounded">
                    {t('aiExtraction.jobDetail.useCaseCardBadge')}
                  </span>
                </div>
                <div className="space-y-2">
                  {payloads.deliverables.map((deliverable: any, idx: number) => (
                    <div key={idx} className="bg-background/50 rounded-lg p-3 border border-border">
                      <div className="flex items-start justify-between">
                        <h6 className="font-semibold text-foreground flex-1">{deliverable.name}</h6>
                        <div className="flex items-center space-x-2 ml-2">
                          {deliverable.deliverable_type && (
                            <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">
                              {deliverable.deliverable_type}
                            </span>
                          )}
                          {deliverable.status && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              deliverable.status === 'completed' ? 'bg-green-100 text-green-800' :
                              deliverable.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {deliverable.status}
                            </span>
                          )}
                        </div>
                      </div>
                      {deliverable.description && (
                        <p className="text-sm text-muted-foreground mt-1">{deliverable.description}</p>
                      )}
                      {deliverable.notes && (
                        <p className="text-sm text-foreground mt-2">
                          <strong>{t('global.notes')}:</strong> {deliverable.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-6" data-tour="genie-job-detail">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border pb-4">
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <FileText className="h-6 w-6 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">{job.document_name}</h3>
          </div>
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <span>
              {t('aiExtraction.jobDetail.createdAt')} {formatDate(job.created_at)}
            </span>
            {job.updated_at && (
              <span>
                {t('aiExtraction.jobDetail.updatedAt')} {formatDate(job.updated_at)}
              </span>
            )}
          </div>
        </div>
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
          job.status === 'review_required' ? 'bg-orange-100 text-orange-800' :
          job.status === 'completed' ? 'bg-green-100 text-green-800' :
          job.status === 'failed' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {job.status.replace('_', ' ')}
        </span>
      </div>

      {/* Job Info */}
      <div className="flex items-center gap-6 text-sm">
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t('aiExtraction.jobDetail.typeLabel')}
          </label>
          <p className="text-sm text-foreground">.{job.context?.original_filename?.split('.').pop() || job.document_type || 'txt'}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t('aiExtraction.jobDetail.sizeLabel')}
          </label>
          <p className="text-sm text-foreground">{job.document_size_kb ? `${Number(job.document_size_kb).toFixed(2)} KB` : 'N/A'}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t('aiExtraction.jobDetail.requestedActionLabel')}
          </label>
          <p className="text-sm text-foreground capitalize">{job.requested_action.replace(/_/g, ' ')}</p>
        </div>
      </div>

      {/* Error Message */}
      {job.error_message && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 min-w-0 overflow-hidden">
          <div className="flex items-start space-x-2 min-w-0">
            <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-destructive">
                {t('aiExtraction.jobDetail.errorTitle')}
              </h4>
              <p className="text-sm text-destructive/90 mt-1 break-words break-all">{job.error_message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Preparation Result */}
      {job.status === 'review_required' && renderPreparationResult()}

      {/* Actions */}
      {job.status === 'review_required' && (
        <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border" data-tour="genie-job-actions">
          <Button
            onClick={onReject}
            variant="outline"
            disabled={isProcessing}
            className="flex items-center space-x-2"
          >
            <XCircle className="h-5 w-5" />
            <span>{t('aiExtraction.jobDetail.cancel')}</span>
          </Button>
          <Button
            onClick={onApprove}
            disabled={isProcessing}
            className="flex items-center space-x-2 bg-[#FF5733] hover:bg-[#E04826] text-white"
          >
            <CheckCircle className="h-5 w-5" />
            <span>{t('aiExtraction.jobDetail.approve')}</span>
          </Button>
        </div>
      )}

      {/* Processing Status */}
      {(job.status === 'pending' || job.status === 'processing') && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Info className="h-5 w-5 text-primary animate-pulse" />
            <p className="text-sm text-muted-foreground">
              {job.status === 'pending' 
                ? t('aiExtraction.jobDetail.statusPending')
                : t('aiExtraction.jobDetail.statusProcessing')}
            </p>
          </div>
        </div>
      )}

      {/* Completed Status */}
      {job.status === 'completed' && (
        <div className="space-y-4">
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <p className="text-sm text-muted-foreground">
                {t('aiExtraction.jobDetail.completedMessage')}
              </p>
            </div>
            {job.extraction_result && (
              <div className="text-xs text-muted-foreground mt-2 space-y-1">
                {job.extraction_result.document_id && (
                  <p>{t('aiExtraction.jobDetail.documentAttached')}</p>
                )}
                {job.extraction_result.personas_count > 0 && (
                  <p>{t('aiExtraction.jobDetail.personasCreated', { count: job.extraction_result.personas_count })}</p>
                )}
                {job.extraction_result.qualification_created && (
                  <p>{t('aiExtraction.jobDetail.qualificationCreated')}</p>
                )}
                {job.extraction_result.blockers_count > 0 && (
                  <p>{t('aiExtraction.jobDetail.blockersIdentifiedCount', { count: job.extraction_result.blockers_count })}</p>
                )}
                {job.extraction_result.deliverables_count > 0 && (
                  <p>{t('aiExtraction.jobDetail.deliverablesCreated', { count: job.extraction_result.deliverables_count })}</p>
                )}
              </div>
            )}
          </div>
          
          {/* Navigation Button to Created/Updated Entity */}
          {getEntityNavigation() && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={() => {
                  const nav = getEntityNavigation();
                  if (nav) navigate(nav.path);
                }}
                className="flex items-center space-x-2 bg-[#FF5733] hover:bg-[#E04826] text-white"
              >
                <span>{getEntityNavigation()?.label}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIExtractionJobDetail;

